import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { AckSummary, MessageStatus } from '@/hooks/useOpsDashboard';

export function Th({ children }: { children: ReactNode }) {
  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      {children}
    </th>
  );
}

export function formatOpsDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('da-DK');
}

export function StatusBadge({ status }: { status: MessageStatus }) {
  const cls: Partial<Record<MessageStatus, string>> = {
    received: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    validated: 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
    queued: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    sent: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    delivered: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    acknowledged: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    failed: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    rejected: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        cls[status] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
    >
      {status}
    </span>
  );
}

export function AckBadge({ ack }: { ack: AckSummary | null }) {
  if (!ack) {
    return <span className="text-gray-400 dark:text-gray-500">—</span>;
  }
  const cls: Record<string, string> = {
    AA: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    AE: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    AR: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        cls[ack.ackType] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
      title={ack.errorMessage ?? undefined}
    >
      {ack.ackType}
    </span>
  );
}

export function AckMatchBadge({ status }: { status: AckSummary['matchStatus'] }) {
  if (status === 'unmatched') {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        Umatchet
      </span>
    );
  }
  if (status === 'matched') {
    return (
      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
        Matchet
      </span>
    );
  }
  return <span className="text-gray-400 dark:text-gray-500">—</span>;
}

export function OpsLoading() {
  return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );
}

export function OpsError({ message }: { message: string }) {
  return (
    <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
      {message}
    </div>
  );
}

export function OpsTable({ children }: { children: ReactNode }) {
  return (
    <div className="mt-6 overflow-hidden sm:rounded-md">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">{children}</table>
      </div>
    </div>
  );
}

export function OpsPagination({
  total,
  limit,
  offset,
  onOffsetChange,
}: {
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + limit, total);
  const prevDisabled = offset <= 0;
  const nextDisabled = offset + limit >= total;

  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {total === 0 ? '0' : `${from}–${to} of ${total}`}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={prevDisabled}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
          className={cn(
            'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
            prevDisabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700',
          )}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={nextDisabled}
          onClick={() => onOffsetChange(offset + limit)}
          className={cn(
            'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
            nextDisabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-gray-50 dark:hover:bg-gray-700',
          )}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function OpsEmpty({ description }: { description: string }) {
  return (
    <div className="mt-8 rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
}
